// U5 tone and length as a diff: what happens to a rewrite when it comes back. It is offered against
// the exact text it was made from; if the owner changed the draft meanwhile (or while the diff is
// shown), the rewrite is set aside with a note, never written over their edits. Pure, for tests.
import { TONE_LABELS, type RewriteResult, type Tone } from "@shared/unstick";

export type ToneOutcome =
  | { kind: "offer"; tone: Tone; before: string; after: string; by: RewriteResult["by"] }
  | { kind: "note"; message: string };

export const CHANGED_MEANWHILE =
  "Your draft changed while it was being rewritten, so the rewrite was set aside. Try again on the new text.";

export function settleRewrite(input: {
  tone: Tone;
  /** The text the rewrite was asked for. */
  base: string;
  /** The text on screen now. */
  current: string;
  result: RewriteResult;
}): ToneOutcome {
  if (input.current !== input.base) return { kind: "note", message: CHANGED_MEANWHILE };
  if (!input.result.changed || input.result.text.trim() === input.base.trim())
    return { kind: "note", message: `Nothing to change for “${TONE_LABELS[input.tone]}”.` };
  return {
    kind: "offer",
    tone: input.tone,
    before: input.base,
    after: input.result.text,
    by: input.result.by,
  };
}

/** An offer can be taken only while the draft is still the text it was made from. */
export function canAccept(offer: { before: string }, current: string): boolean {
  return offer.before === current;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Plain text as the composer's HTML: blank lines make paragraphs, single newlines line breaks. */
export function plainTextHtml(text: string): string {
  return text
    .replace(/\r\n/g, "\n")
    .trim()
    .split(/\n{2,}/)
    .map((p) => `<p>${p.split("\n").map(escapeHtml).join("<br>")}</p>`)
    .join("");
}

/** The composer body as plain text for a rewrite: paragraphs stay paragraphs. */
export function composerText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|blockquote|div|h\d)>/gi, "\n\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Whether accepting a plain-text rewrite would drop formatting from the composer's body. */
export function hasFormatting(html: string): boolean {
  return /<(strong|b|em|i|a|ul|ol|li|blockquote|h\d)\b/i.test(html);
}
